<template>
  <div class="analysis-page">
    <section class="coming-soon" aria-labelledby="analysis-title">
      <div class="hero">
        <div class="hero-copy">
          <span class="eyebrow">NGO Analysis</span>
          <span class="status-badge"><span></span> In development</span>
          <h1 id="analysis-title">A clearer picture.<br /><em>Coming soon.</em></h1>
          <p class="intro">
            We're building a new way to explore the NGO landscape. Discover patterns, understand
            coverage, and turn registry data into meaningful insights.
          </p>
          <router-link :to="{ name: 'registered ngos' }" class="registry-link"
            >Explore the NGO registry <span aria-hidden="true">↗</span></router-link
          >
          <p class="availability">The analysis workspace is under development. Stay tuned.</p>
        </div>
        <div class="insight-art" aria-hidden="true">
          <div class="orbit orbit--outer"></div>
          <div class="orbit orbit--inner"></div>
          <span class="spark spark--top">✦</span><span class="spark spark--bottom">✦</span>
          <div class="preview-card">
            <div class="preview-header">
              <span class="preview-icon"
                ><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
                  <path d="M5 19V12M12 19V5M19 19V9" stroke-linecap="round" /></svg
              ></span>
              <span>Insights, taking shape</span><span class="preview-dots">•••</span>
            </div>
            <div class="preview-lines"><span></span><span></span></div>
            <div class="chart">
              <span
                v-for="(height, index) in [28, 43, 36, 62, 51, 76, 68, 92]"
                :key="index"
                :style="{ height: `${height}%` }"
              ></span>
            </div>
            <div class="preview-footer"><span>NGO ANALYSIS</span><span>ON THE HORIZON</span></div>
          </div>
          <div class="coverage-card">
            <div class="coverage-ring">↗</div>
            <div><strong>A wider perspective</strong><span>From records to insights</span></div>
          </div>
          <span class="art-caption">A NEW PERSPECTIVE IS ON ITS WAY</span>
        </div>
      </div>
      <div class="upcoming">
        <div class="upcoming-heading">
          <span class="eyebrow">What's ahead</span>
          <h2>Built for the bigger picture.</h2>
        </div>
        <div class="feature">
          <span class="feature-number">01</span>
          <div>
            <h3>Regional coverage</h3>
            <p>Explore the NGO footprint across states and zones.</p>
          </div>
        </div>
        <div class="feature">
          <span class="feature-number">02</span>
          <div>
            <h3>Organization insights</h3>
            <p>Understand the mix of organizations and focus areas.</p>
          </div>
        </div>
        <div class="feature">
          <span class="feature-number">03</span>
          <div>
            <h3>Registry trends</h3>
            <p>See how the NGO landscape evolves over time.</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ViewsAnalysis',
};
</script>

<style lang="scss" scoped>
.analysis-page {
  padding: clamp(1rem, 3vw, 3rem) 1rem;
  background: #f5f7f3;
  color: #193d30;
}
.coming-soon {
  max-width: 1240px;
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid #dfe7dc;
  border-radius: 28px;
  background: #fdfefb;
  box-shadow: 0 18px 60px rgba(28, 57, 40, 0.06);
}
.hero {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  min-height: 550px;
}
.hero-copy {
  padding: clamp(2rem, 4vw, 4rem);
  align-self: center;
}
.eyebrow {
  display: block;
  color: #58715f;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 2rem;
  padding: 7px 12px;
  border: 1px solid #d8e3ce;
  border-radius: 30px;
  color: #476333;
  background: #f0f5e9;
  font-size: 0.75rem;
  font-weight: 600;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #6c873c;
  }
}
h1 {
  margin: 1.25rem 0;
  color: #193d30;
  font-size: clamp(2.4rem, 3.7vw, 3.5rem);
  font-weight: 600;
  line-height: 1.14;
  letter-spacing: -0.045em;
  em {
    color: #648044;
    font-family: Georgia, serif;
    font-weight: 400;
  }
}
.intro {
  max-width: 440px;
  margin-bottom: 1.75rem;
  color: #617167;
  font-size: 0.96rem;
  line-height: 1.85;
}
.registry-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: 14px 20px;
  border-radius: 9px;
  background: #224e39;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  transition: background 180ms ease, transform 180ms ease;
  span {
    font-size: 1.2rem;
  }
  &:hover {
    background: #173b2a;
    color: #fff;
    text-decoration: none;
    transform: translateY(-2px);
  }
  &:focus-visible {
    outline: 3px solid #849b50;
    outline-offset: 4px;
  }
}
.availability {
  margin: 1rem 0 0;
  max-width: 340px;
  color: #6c796e;
  font-size: 0.74rem;
  line-height: 1.7;
}
.insight-art {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
  background: radial-gradient(ellipse at 65% 35%, #335941 0%, #173c2e 55%, #102c23 100%);
}
.orbit {
  position: absolute;
  width: 440px;
  height: 440px;
  border: 1px solid rgba(216, 235, 179, 0.12);
  border-radius: 50%;
}
.orbit--outer {
  width: 600px;
  height: 600px;
}
.spark {
  position: absolute;
  color: #ccda9a;
  font-size: 1.5rem;
}
.spark--top {
  top: 12%;
  right: 15%;
}
.spark--bottom {
  bottom: 18%;
  left: 10%;
  font-size: 1rem;
}
.preview-card {
  position: relative;
  width: 76%;
  max-width: 365px;
  padding: 22px;
  border: 1px solid rgba(229, 238, 218, 0.3);
  border-radius: 18px;
  background: linear-gradient(135deg, #f7f9ed, #e8eedc);
  box-shadow: 0 30px 60px rgba(0, 15, 8, 0.3);
  transform: rotate(-6deg) translateY(-15px);
}
.preview-header {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 0.7rem;
  font-weight: 600;
}
.preview-icon {
  display: flex;
  padding: 7px;
  border-radius: 8px;
  background: #e1e9d4;
}
.preview-icon svg {
  width: 20px;
  height: 20px;
}
.preview-dots {
  margin-left: auto;
  letter-spacing: 2px;
}
.preview-lines {
  display: grid;
  gap: 8px;
  margin: 24px 0 20px;
}
.preview-lines span {
  width: 33%;
  height: 9px;
  border-radius: 4px;
  background: #b7c5a3;
}
.preview-lines span + span {
  width: 52%;
  height: 5px;
  background: #d4ddc8;
}
.chart {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  height: 145px;
  padding: 0 5px;
  border-bottom: 1px solid #c9d5bb;
  background: repeating-linear-gradient(
    to top,
    transparent 0,
    transparent 35px,
    #dce4d3 36px,
    transparent 37px
  );
  span {
    flex: 1;
    border-radius: 5px 5px 0 0;
    background: linear-gradient(to top, #345c40, #829965);
  }
  span:last-child {
    background: linear-gradient(to top, #92ab62, #c0d28c);
  }
}
.preview-footer {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 14px;
  color: #5e7253;
  font-size: 0.5rem;
  letter-spacing: 0.1em;
}
.coverage-card {
  position: absolute;
  right: 6%;
  bottom: 19%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border: 1px solid #d5dfc5;
  border-radius: 13px;
  background: #f7faef;
  box-shadow: 0 16px 32px rgba(0, 15, 8, 0.2);
  transform: rotate(4deg);
  strong,
  span {
    display: block;
  }
  strong {
    font-size: 0.75rem;
    font-weight: 600;
  }
  div > span {
    margin-top: 4px;
    color: #6b7c61;
    font-size: 0.65rem;
  }
}
.coverage-ring {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 4px solid #d8e3c4;
  border-top-color: #668548;
  border-right-color: #668548;
  border-radius: 50%;
}
.art-caption {
  position: absolute;
  bottom: 7%;
  color: #bbcdb5;
  font-size: 0.55rem;
  letter-spacing: 0.2em;
}
.upcoming {
  display: grid;
  grid-template-columns: 1.1fr 1fr 1fr 1fr;
  gap: 1.5rem;
  padding: 2rem clamp(1.5rem, 3vw, 3rem);
  border-top: 1px solid #e3e9de;
}
.upcoming-heading h2 {
  margin: 10px 0 0;
  max-width: 180px;
  font-size: 1.15rem;
  line-height: 1.45;
  color: #294835;
}
.feature {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.feature-number {
  padding-top: 2px;
  color: #6b8158;
  font-size: 0.65rem;
}
.feature h3 {
  margin: 0 0 9px;
  font-size: 0.83rem;
  font-weight: 600;
  color: #294835;
}
.feature p {
  margin: 0;
  color: #6b796d;
  font-size: 0.76rem;
  line-height: 1.75;
}
@media (max-width: 991px) {
  .hero {
    grid-template-columns: 1fr 1fr;
  }
  .hero-copy {
    padding: 2rem;
  }
  .upcoming {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem;
  }
  .preview-card {
    padding: 16px;
    width: 82%;
  }
  .coverage-card {
    right: 5%;
    bottom: 16%;
    padding: 12px;
  }
}
@media (max-width: 640px) {
  .coming-soon {
    border-radius: 20px;
  }
  .hero {
    grid-template-columns: 1fr;
  }
  .hero-copy {
    padding: 2rem 1.5rem;
  }
  .insight-art {
    min-height: 400px;
  }
  .preview-card {
    width: 75%;
    max-width: 310px;
  }
  .coverage-card {
    bottom: 14%;
    right: 7%;
  }
  .art-caption {
    bottom: 5%;
  }
  .upcoming {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  .upcoming-heading h2 {
    max-width: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .registry-link {
    transition: none;
  }
}
</style>
